import isEmpty from 'lodash/isEmpty';
import { getSellingPrice } from '@lib/get-selling-price';
import { Product, VariationValue } from '@type/index';

// Helper to safely extract image URL from Attachment or string
const getImageUrl = (img: any): string => {
  if (!img) return '';
  if (typeof img === 'string') return img;
  return img.original || '';
};

/** Check whether an attribute value represents a color via attribute.type. */
function isColorAttribute(attr: any): boolean {
  return attr?.attribute?.type === 'color';
}

/**
 * Extract color and non-color info from a variation_option's attributes.
 *
 * When a `selectedColorVariation` (from product.variations[]) is provided it is
 * used as the authoritative color source — its `attribute.type` is always set
 * correctly by the API, so no regex or heuristics are needed.
 */
function extractVariationDetails(
  variation: any,
  selectedColorVariation?: VariationValue | null,
): {
  color: { colorName: string; colorCode: string } | null;
  variationTitle: string;
} {
  const attrs: any[] = variation?.attributes ?? [];

  // Build the color from the authoritative product.variations[] entry when available
  let color: { colorName: string; colorCode: string } | null =
    selectedColorVariation
      ? {
          colorName: selectedColorVariation.value,
          colorCode: selectedColorVariation.meta,
        }
      : null;

  const nonColorParts: string[] = [];

  attrs.forEach((attr: any) => {
    if (isColorAttribute(attr)) {
      // Only fall back to variation_option attribute if we have no authoritative color
      if (!color) {
        color = {
          colorName: attr.value ?? '',
          colorCode: attr.meta ?? '',
        };
      }
    } else {
      nonColorParts.push(attr.value ?? '');
    }
  });

  // If no attributes at all, fall back to the title (but skip "Default")
  if (attrs.length === 0) {
    const title = variation?.title || '';
    const isDefault = !title || title.toLowerCase() === 'default';
    return {
      color,
      variationTitle: isDefault ? '' : title,
    };
  }

  return {
    color,
    variationTitle: nonColorParts.join(' / '),
  };
}

export function generateCartItem(
  item: Product,
  variation: any,
  selectedColorVariation?: VariationValue | null,
) {
  const {
    id,
    name = '',
    slug = '',
    image = { original: '' },
    quantity,
    unit,
    type,
  } = item || {};

  const commonProps = {
    slug,
    unit,
    // type.name is set for regular product pages; pivot.brand_name is set for
    // order products where the brand is recorded on the order line item.
    brandName: type?.name || (item as any)?.pivot?.brand_name,
    productId: id,
  };

  // Variable product with a matched variation_option
  if (!isEmpty(variation)) {
    // Brand sale included. Must match what checkout will charge, or the order
    // is rejected by server-side price validation at the payment step.
    // originalPrice is carried so the cart can strike through the pre-discount
    // figure; it is display only and never sent to the order endpoint.
    const {
      sellingPrice: variationPrice,
      originalPrice: variationOriginalPrice,
    } = getSellingPrice(variation);
    const { color, variationTitle } = extractVariationDetails(
      variation,
      selectedColorVariation,
    );

    // Image priority:
    //   1. variation_option.images[0]          (new API: images array on the option)
    //   2. selectedColorVariation.images[0]    (variations[n].images — color-level)
    //   3. variation_option.image              (legacy single-image field)
    //   4. product.image                       (top-level image field)
    //   5. product.images[0].original          (media-library images array)
    const resolvedImage =
      getImageUrl(variation?.images?.[0]) ||
      getImageUrl(selectedColorVariation?.images?.[0]) ||
      getImageUrl(variation?.image) ||
      getImageUrl(image) ||
      getImageUrl((item as any)?.images?.[0]);

    // The variation_option.id already uniquely encodes the full combination
    // (color + size + any other attributes), so we use it as the cart item ID.
    return {
      id: `${id}.${variation.id}`,
      name,
      ...commonProps,
      stock: variation.quantity,
      price: variationPrice,
      originalPrice: variationOriginalPrice ?? undefined,
      image: resolvedImage,
      variationId: variation.id,
      sku: variation.sku,
      variationTitle: variationTitle || undefined,
      product_variation: variation,
      color: color || undefined,
    };
  }

  // Simple product (no variation)
  const { sellingPrice: productPrice, originalPrice: productOriginalPrice } =
    getSellingPrice(item);
  return {
    id,
    name,
    ...commonProps,
    image: getImageUrl(image) || getImageUrl((item as any)?.images?.[0]),
    stock: quantity,
    price: productPrice,
    originalPrice: productOriginalPrice ?? undefined,
  };
}
